import { memo } from 'react';

import { MessageSquareIcon } from '@air/design-icons';
import { Avatar } from '@air/primitive-avatar';
import { formatInitials, formatUpdatedAt, parseAirDateToISO } from '@air/utilities';
import { tailwindVariants } from '@air/utils-tailwind';

const COMMENT_AVATAR_SIZE = 24;

const workspaceAgentCommentCard = tailwindVariants({
  base: 'relative w-full rounded-6 ads:bg-raised bg-gray-2 px-2.5 py-2 text-left ads:ring-subtle ring-1 ring-gray-3 ring-inset transition-colors',
  defaultVariants: {
    isInteractive: false,
  },
  variants: {
    isInteractive: {
      true: 'cursor-pointer ads:hover:bg-hover-subtle hover:bg-gray-3 ads:hover:ring-default hover:ring-gray-4 focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-8',
    },
  },
});

const getAuthorInitials = (authorName: string, authorInitials?: string) => {
  if (authorInitials) {
    return authorInitials;
  }

  const parts = authorName.trim().split(/\s+/);

  if (parts.length >= 2) {
    return formatInitials(parts[0], parts.at(-1));
  }

  return authorName.slice(0, 2).toUpperCase();
};

export type WorkspaceAgentCommentCardProps = {
  authorInitials?: string;
  authorName: string;
  avatarUrl?: string;
  body: string;
  colorSeed: string;
  createdAt: string;
  'data-testid'?: string;
  onOpen?: () => void;
  staleVersion?: boolean;
};

/**
 * A cited discussion comment — avatar, author, age, and body. Rendered as a button only when there
 * is somewhere to go, matching `WorkspaceAgentQuoteCard`'s read-only rule.
 */
export const WorkspaceAgentCommentCard = memo(
  ({
    authorInitials,
    authorName,
    avatarUrl,
    body,
    colorSeed,
    createdAt,
    'data-testid': testId,
    onOpen,
    staleVersion,
  }: WorkspaceAgentCommentCardProps) => {
    const initials = getAuthorInitials(authorName, authorInitials);
    const createdAtLabel = formatUpdatedAt(parseAirDateToISO(createdAt), { addSuffix: true });

    const content = (
      <>
        <MessageSquareIcon aria-hidden className="absolute top-2 right-2 ads:text-icon-subtle text-gray-8" size="xs" />
        <div className="flex gap-2 pr-5">
          <Avatar
            alt={authorName}
            className="shrink-0"
            colorSeed={colorSeed}
            size={COMMENT_AVATAR_SIZE}
            src={avatarUrl}
            text={initials}
          />
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
              <span
                className="min-w-0 truncate font-medium ads:text-primary text-12 text-gray-12 leading-4"
                title={authorName}
              >
                {authorName}
              </span>
              <span className="shrink-0 ads:text-metadata text-12 text-gray-10 leading-4">
                {createdAtLabel}
                {staleVersion ? ' · older version' : null}
              </span>
            </div>
            <p className="mt-0.5 ads:text-secondary text-12 text-gray-11 leading-4">{body}</p>
          </div>
        </div>
      </>
    );

    if (!onOpen) {
      return (
        <div className={workspaceAgentCommentCard()} data-testid={testId}>
          {content}
        </div>
      );
    }

    return (
      <button
        className={workspaceAgentCommentCard({ isInteractive: true })}
        data-testid={testId}
        onClick={onOpen}
        type="button"
      >
        {content}
      </button>
    );
  },
);

WorkspaceAgentCommentCard.displayName = 'WorkspaceAgentCommentCard';
